import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteVideoResult,
  DeleteVideoOptions,
  PatchVideoResult,
  PatchVideoOptions,
  GetVideoOptions,
  GetVideoResult,
  getVideo,
  DeleteVideoFields,
  PatchVideoFields,
  GetVideoFields,
  deleteVideo,
  patchVideo,
} from '../fetchers/video';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoOptions<Fields extends GetVideoFields> = GetVideoOptions<Fields>;

/**
 * Get a specific video
 * This method returns a single video.
 */

export function useGetVideo<Fields extends GetVideoFields>(
  getOptions: UseGetVideoOptions<Fields> | (() => UseGetVideoOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/videos/${options.where.videoId}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getVideo({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideo, {
    endpoint: '/videos/:videoId',
    method: 'GET',
  });
}

/**
 * Get a specific video
 * This method returns a single video.
 */

export function useGetVideoLazy<Fields extends GetVideoFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}${serializeQuery(options)}`,
          getVideo({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoLazy, {
    endpoint: '/videos/:videoId',
    method: 'GET',
  });
}

type UseDeleteVideoOptions<Fields extends DeleteVideoFields> = DeleteVideoOptions<Fields>;

/**
 * Delete a video
 * This method deletes the specified video. The authenticated user must be the owner of the video.
 */

export function useDeleteVideo<Fields extends DeleteVideoFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteVideoResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteVideoOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}${serializeQuery(options)}`,
          deleteVideo({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideo, {
    endpoint: '/videos/:videoId',
    method: 'DELETE',
  });
}

type UsePatchVideoOptions<Fields extends PatchVideoFields> = PatchVideoOptions<Fields>;

/**
 * Edit a video
 * This method edits the specified video.
 */

export function usePatchVideo<Fields extends PatchVideoFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchVideoResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchVideoOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}${serializeQuery(options)}`,
          patchVideo({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchVideo, {
    endpoint: '/videos/:videoId',
    method: 'PATCH',
  });
}
